import { useState } from "react";
import { motion, AnimatePresence, stagger } from "framer-motion";
import { HiChevronDoubleRight, HiOutlineArrowUpRight } from "react-icons/hi2";
import RobotImg from "../../Assets/RobotImg.png";
import sidelogo from "../../Assets/joinNow.png";

const WhoWeAre = () => {
  const [activeTab, setActiveTab] = useState("Our Vision");

  const tabs = ["Our Mission", "Our Vision", "Key Feature"];

  // Parent container variants
  const sectionVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.2 },
    },
  };

  const childVariants = {
    hidden: { opacity: 0, y: 35 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  // Staggered letter animation for main heading
  const letterContainer = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.04 },
    },
  };

  const letterItem = {
    hidden: { opacity: 0, y: 60, rotateX: -90 },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: { type: "spring", damping: 12, stiffness: 200 },
    },
  };

  // Tab content fade + slide variants
  const contentVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
    exit: { opacity: 0, x: 30, transition: { duration: 0.4 } },
  };

  // Split text into letters for animation
  const headingText1 = "Explore The AI Power";
  const headingText2 = "& Our Innovative Solutions";

  return (
    <motion.section
      id="about"
      className="relative w-full px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 py-4 sm:py-6 md:py-8 lg:py-10 xl:py-12 bg-white overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-80px" }}
      variants={sectionVariants}
    >
      <motion.div className="w-full mx-auto relative z-10">
        {/* Header Badge */}
        <motion.div
          className="flex items-center gap-2 mb-1 sm:mb-2 md:mb-3 lg:mb-4 xl:mb-5"
          variants={childVariants}
        >
          <motion.img
            src={sidelogo}
            alt=""
            className="w-8 sm:w-auto"
            whileHover={{
              scale: 1.15,
              rotate: 12,
              transition: { duration: 0.4 },
            }}
          />
          <span className="text-xs sm:text-sm font-bold tracking-[0.2em] text-brandDark uppercase">
            Who We Are
          </span>
        </motion.div>

        {/* Main Heading with Letter-by-Letter Stagger */}
        <motion.h2
          className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-semibold text-brandDark leading-tight mb-4 sm:mb-6 md:mb-8 lg:mb-10 xl:mb-12 max-w-full lg:max-w-4xl"
          variants={letterContainer}
        >
          {headingText1.split("").map((char, i) => (
            <motion.span
              key={`line1-${i}`}
              variants={letterItem}
              className="inline-block"
              style={{ whiteSpace: char === " " ? "pre" : "normal" }}
            >
              {char}
            </motion.span>
          ))}
          <br />
          {headingText2.split("").map((char, i) => (
            <motion.span
              key={`line2-${i}`}
              variants={letterItem}
              className="inline-block"
              style={{ whiteSpace: char === " " ? "pre" : "normal" }}
            >
              {char === "Innovative" ? (
                <span className="text-brandOrange">{char}</span>
              ) : (
                char
              )}
            </motion.span>
          ))}
        </motion.h2>

        {/* Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-start">
          {/* Tabs */}
          <div className="lg:col-span-3 flex flex-col gap-4 lg:gap-6">
            {tabs.map((tab) => (
              <motion.button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`w-full py-4 sm:py-6 px-6 sm:px-8 rounded-full text-base sm:text-lg lg:text-xl font-semibold transition-all duration-300 text-center overflow-hidden relative ${
                  activeTab === tab
                    ? "bg-brandOrange text-white shadow-xl shadow-orange-300/40"
                    : "bg-[#FFDED2] text-brandOrange hover:bg-[#ffcec2]"
                }`}
                whileHover={{
                  scale: 1.06,
                  boxShadow:
                    activeTab === tab
                      ? "0 25px 50px -12px rgba(249, 115, 22, 0.4)"
                      : "0 15px 30px -10px rgba(249, 115, 22, 0.25)",
                }}
                whileTap={{ scale: 0.94 }}
                layout
              >
                {tab}
                {activeTab === tab && (
                  <motion.div
                    className="absolute inset-0 bg-white/10"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1.5, opacity: 0.15 }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      repeatType: "reverse",
                    }}
                  />
                )}
              </motion.button>
            ))}
          </div>

          {/* Content Area with AnimatePresence */}
          <div className="lg:col-span-4 flex flex-col gap-4 sm:gap-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                variants={contentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="contents"
              >
                <p className="text-[#041B2F] w-full xl:w-[85%] text-base sm:text-lg leading-relaxed opacity-80">
                  Duise sagittis accumsan magna on adipiscine laoreet ultrices
                  magna consectetur eiaculis rutrum mobie habitasse orcids
                  libero porttitor molestie mollise.
                </p>

                <ul className="space-y-3 sm:space-y-4">
                  {[
                    "Real-World Transformations",
                    "Innovative AI Solutions",
                    "Bot Brains, Business Boosters",
                  ].map((item, idx) => (
                    <motion.li
                      key={idx}
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -40 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        delay: 0.2 + idx * 0.18,
                        type: "spring",
                        stiffness: 120,
                      }}
                    >
                      <HiChevronDoubleRight className="text-brandOrange text-lg sm:text-xl" />
                      <span className="text-base sm:text-lg font-semibold text-brandDark">
                        {item}
                      </span>
                    </motion.li>
                  ))}
                </ul>

                <motion.button
                  className="flex items-center justify-center gap-3 bg-brandDark text-white py-3 sm:py-4 px-8 sm:px-10 rounded-full w-fit hover:bg-black transition-all group mt-2 sm:mt-4 mx-auto lg:mx-0 relative overflow-hidden"
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.92 }}
                  transition={{ type: "spring", stiffness: 400, damping: 15 }}
                >
                  <span className="font-semibold relative z-10">
                    Explore Now
                  </span>
                  <HiOutlineArrowUpRight className="text-xl group-hover:translate-x-2 group-hover:-translate-y-2 transition-transform duration-300 relative z-10" />

                  {/* Shine / gradient hover effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: "100%" }}
                    transition={{ duration: 0.7, ease: "easeInOut" }}
                  />
                </motion.button>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Image - Enhanced entrance + hover */}
          <motion.div
            className="lg:col-span-5 flex justify-center lg:justify-end -mt-4 md:-mt-8 xl:-mt-24"
            initial={{ opacity: 0, scale: 0.88, y: 60 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{
              duration: 1.3,
              delay: 0.5,
              type: "spring",
              stiffness: 80,
            }}
          >
            <div className="rounded-[2rem] sm:rounded-[3rem] overflow-hidden w-full aspect-square flex items-center justify-center shadow-2xl shadow-orange-200/30">
              <motion.img
                src={RobotImg}
                alt="AI Assistant Bot"
                className="w-full h-full object-contain"
                whileHover={{
                  scale: 1.08,
                  rotate: 3,
                  transition: { duration: 0.6 },
                }}
                transition={{ duration: 0.4 }}
              />
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.section>
  );
};

export default WhoWeAre;
